import { EmptyState, FlexRow, MaterialIcon, useMoment } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect } from 'react';
import { useRecoilValue, useResetRecoilState } from 'recoil';
import { DELIVERY_REPORT_TABLE_COLUMNS, EXTENDED_DELIVERY_REPORT_TABLE_COLUMNS } from '../../../constants/reports';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import { getDeliveryReportFile } from '../../../services/reportsService';
import { apiParamsState, isAllWorkspacesSelect } from '../../../storage/app';
import { activeDeliveryState } from '../../../storage/reports';
import { DeliveryReport } from '../../../types/reports';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';

type Props = {
  extended?: boolean;
  page: number;
  states?: DeliveryReport[];
  totalItems: number;
  onPageChange: (nextPage: number) => void;
};

function DeliveryReportTable({ states, page, onPageChange, totalItems, extended = false }: Readonly<Props>) {
  const { formatDateFromTs } = useMoment();
  const { showFailFeedback } = useFeedback();
  const { theme } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const isAllWorkspacesSelected = useRecoilValue(isAllWorkspacesSelect);
  const resetActiveDeliveryState = useResetRecoilState(activeDeliveryState);

  const downloadReport = async (reportId: string) => {
    if (!apiParams) return;
    const { url, error } = await getDeliveryReportFile(
      reportId,
      apiParams.clientId,
      isAllWorkspacesSelected ? undefined : apiParams.subClientId,
    );
    if (!error && url) {
      window.open(url, '_blank');
    } else {
      showFailFeedback(error || 'Ocurrió un error al descargar el reporte, inténtalo más tarde.');
    }
  };

  useEffect(() => {
    return () => {
      resetActiveDeliveryState();
    };
  }, []);

  if (!states?.length) {
    return <EmptyState title="No cuenta con registros de entregas." />;
  }

  return (
    <StyledPaginatedTable
      columns={extended ? EXTENDED_DELIVERY_REPORT_TABLE_COLUMNS : DELIVERY_REPORT_TABLE_COLUMNS}
      data={states.map((report, i) => {
        const name = report.asset?.label || '-';
        const items = [
          report.statusDescription,
          report.processedAt ? formatDateFromTs(report.processedAt) : '-',
          <FlexRow
            key={`details-${report._id}`}
            gap={theme.sizing.scale0}
          >
            <StyledTooltip
              content={'Descargar'}
              showArrow={false}
            >
              <Button
                size="compact"
                shape="square"
                onClick={() => downloadReport(report._id)}
                kind="tertiary"
                disabled={report.status !== 2}
              >
                <MaterialIcon name="download_for_offline" />
              </Button>
            </StyledTooltip>
          </FlexRow>,
        ];
        if (extended) {
          items.unshift(name);
        }
        return {
          items,
        };
      })}
      totalItems={totalItems}
      page={page}
      onPageChange={onPageChange}
      minTableWidthSize={480}
    />
  );
}

export default DeliveryReportTable;
